import { defineStore } from 'pinia';
import liaisonRepository from '../api/liaisonRepository';
import {
  TransitionAction,
  type LiaisonState,
  type HistoryItem,
  type TemplateData,
  type NotificationType,
  type TemplateType,
  type LiaisonTarget,
} from './types';

/** 現況報告の自動送信間隔（分） */
const AUTO_SEND_INTERVAL_MIN = 30;

/** 送信履歴の遡及年数 */
const HISTORY_LOOKBACK_YEARS = 2;

/** 予定時刻変更で現況報告プレビューに影響する遷移アクション */
const PREVIEW_AFFECTING_ACTIONS: TransitionAction[] = [
  TransitionAction.SetEmergencyTime,
  TransitionAction.UnsetEmergencyTime,
  TransitionAction.ResetEmergencyTime,
];

interface Target {
  basinId: string;
  stationId: string;
}

interface StoreState {
  /** 選択可能なリエゾン対象ダム一覧（BEが返すisLiaisonTargetがtrueのstation） */
  targets: LiaisonTarget[];
  /** リエゾン通知タブで選択中の対象（basinId + stationIdのペア） */
  selectedTarget: LiaisonTarget | null;
  /** BEから取得した現在のリエゾン状態。未取得時はnull */
  state: LiaisonState | null;
  /** 送信履歴（新しい順） */
  history: HistoryItem[];
  /** 派遣要請・待機解除のテンプレート */
  templates: {
    dispatchRequest: TemplateData | null;
    standbyRelease: TemplateData | null;
  };
  /** 現況報告プレビュー本文（BE生成の最新ダム諸量埋込済） */
  statusReportPreview: string;
  /** sim_shien.csvで緊急放流が予測されているかどうか */
  hasEmergencyForecast: boolean;
  /** システムが予測した緊急放流時刻 (YYYYMMDDHHMM) */
  emergencyPredictionTime: string | null;
  /** 直近のBE通信がロード中かどうか */
  loading: boolean;
  /** 直近のBE通信エラー */
  error: Error | null;
}

const initialState = (): LiaisonState => ({
  autoSendStatus: 'stopped',
  emergencyTimeStatus: 'unset',
  sendContinuationOriginTime: null,
  sendStopTime: null,
  emergencyReleaseTime: null,
  lastSentAt: null,
  updatedAt: null,
});

export const useLiaisonStore = defineStore('liaison', {
  state: (): StoreState => ({
    targets: [],
    selectedTarget: null,
    state: null,
    history: [],
    templates: {
      dispatchRequest: null,
      standbyRelease: null,
    },
    statusReportPreview: '',
    hasEmergencyForecast: false,
    emergencyPredictionTime: null,
    loading: false,
    error: null,
  }),

  getters: {
    currentState(state): LiaisonState {
      return state.state ?? initialState();
    },
    isSending(state): boolean {
      return state.state?.autoSendStatus === 'sending';
    },
    isEmergencyTimeSet(state): boolean {
      return state.state?.emergencyTimeStatus === 'set';
    },
    /**
     * 次回自動送信予定時刻を HH:MM で返す。
     * lastSentAt を優先し、未送信の場合は sendContinuationOriginTime を起点に30分後を計算する。
     * いずれも YYYYMMDDHHMM 形式が前提。
     */
    nextSendTime(state): string | null {
      const origin = state.state?.lastSentAt || state.state?.sendContinuationOriginTime;
      if (!origin || origin.length < 12) return null;
      const year = Number(origin.slice(0, 4));
      const month = Number(origin.slice(4, 6)) - 1;
      const day = Number(origin.slice(6, 8));
      const hour = Number(origin.slice(8, 10));
      const minute = Number(origin.slice(10, 12));
      if ([year, month, day, hour, minute].some(Number.isNaN)) return null;
      const nextSendDate = new Date(year, month, day, hour, minute);
      nextSendDate.setMinutes(nextSendDate.getMinutes() + AUTO_SEND_INTERVAL_MIN);
      if (Number.isNaN(nextSendDate.getTime())) return null;
      return nextSendDate.toTimeString().slice(0, 5);
    },
  },

  actions: {
    async fetchTargets() {
      const { data } = await liaisonRepository.listTargets();
      this.targets = data.items;
      // 選択中のものが一覧から消えていたらリセット、未選択なら先頭を初期選択
      if (!this.selectedTarget && data.items.length > 0) {
        [this.selectedTarget] = data.items;
      } else if (this.selectedTarget) {
        const stillAvailable = data.items.find(
          (item) => item.basinId === this.selectedTarget?.basinId
            && item.stationId === this.selectedTarget?.stationId,
        );
        if (!stillAvailable) {
          this.selectedTarget = data.items[0] ?? null;
        }
      }
    },

    selectTarget(target: LiaisonTarget) {
      this.selectedTarget = target;
    },

    async fetchState(target: Target) {
      this.loading = true;
      try {
        const { data } = await liaisonRepository.getState(target);
        this.state = data;
      } catch (error) {
        this.error = error as Error;
      } finally {
        this.loading = false;
      }
    },

    async updateState(
      target: Target,
      payload: { action: TransitionAction; emergencyReleaseTime?: string },
    ) {
      const { data } = await liaisonRepository.updateState(target, payload);
      this.state = data;
      if (PREVIEW_AFFECTING_ACTIONS.includes(payload.action)) {
        await this.fetchPreview(target);
      }
    },

    async send(
      target: Target,
      payload: { type: NotificationType; subject?: string; body: string },
    ) {
      const { data } = await liaisonRepository.send(target, payload);
      this.state = data.newState;
      return data;
    },

    async fetchHistory(target: Target) {
      const now = new Date();
      const twoYearsAgo = new Date(now);
      twoYearsAgo.setFullYear(now.getFullYear() - HISTORY_LOOKBACK_YEARS);

      const { data } = await liaisonRepository.listHistory(target, {
        fromIso: twoYearsAgo.toISOString(),
        toIso: now.toISOString(),
      });
      this.history = data.items;
    },

    async fetchTemplate(
      target: { basinId: string },
      templateType: TemplateType,
    ) {
      const { data } = await liaisonRepository.getTemplate({
        basinId: target.basinId,
        templateType,
      });
      this.templates[templateType] = data;
    },

    async fetchAllTemplates(target: { basinId: string }) {
      await Promise.all([
        this.fetchTemplate(target, 'dispatchRequest'),
        this.fetchTemplate(target, 'standbyRelease'),
      ]);
    },

    async updateTemplate(
      target: { basinId: string },
      templateType: TemplateType,
      payload: { subject: string; body: string },
    ) {
      const { data } = await liaisonRepository.updateTemplate(
        { basinId: target.basinId, templateType },
        payload,
      );
      this.templates[templateType] = data;
    },

    async fetchPreview(target: Target) {
      try {
        const { data } = await liaisonRepository.getPreview({
          ...target,
          type: 'statusReport',
        });
        this.statusReportPreview = data.body;
        this.hasEmergencyForecast = data.hasEmergencyForecast;
        this.emergencyPredictionTime = data.emergencyPredictionTime;
      } catch (error) {
        this.error = error as Error;
        this.statusReportPreview = '';
      }
    },
  },
});
